import { findStationById, findStationsBySpace, findStationsWithLocation, findStationByKeypad } from '../firebase/repositories/StationRepository';
import { findSpaceById, findChildSpaces } from '../firebase/repositories/SpaceRepository';
import { findStoriesByStation } from '../firebase/repositories/StoryRepository';
import { findSiteBySlug } from '../firebase/repositories/SiteRepository';
import { toPlace, resolveLang, extractAssetId, hasLanguageContent } from './firestoreAdapter';
import type { Place, FirestorePlaceDoc } from '../types';

const CACHE_TTL_MS = 5 * 60 * 1000;

interface CacheEntry {
  place: Place;
  timestamp: number;
}

const placeCache = new Map<string, CacheEntry>();

function cacheKey(placeId: number | string, language: string, variant: string = 'full'): string {
  return `${placeId}:${language}:${variant}`;
}

function getCached(key: string): Place | null {
  const entry = placeCache.get(key);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    placeCache.delete(key);
    return null;
  }
  return entry.place;
}

function setCache(key: string, place: Place): void {
  placeCache.set(key, { place, timestamp: Date.now() });
}

export const fetchPlaceMetaData = async (placeId: number | string, language: string): Promise<Place | null> => {
  const strId = String(placeId);
  let doc: FirestorePlaceDoc | null = await findStationById(strId);
  if (!doc) doc = await findSpaceById(strId);
  if (!doc) return null;

  const place = toPlace(doc, language);
  if (!place) return null;

  if (doc.spaceType && !doc.stationType) {
    const childStations = await findStationsBySpace(strId);
    const childSpaces = await findChildSpaces(strId);
    const allChildren = [
      ...childSpaces.map((s: FirestorePlaceDoc) => ({ ...s, _isSpace: true })),
      ...childStations,
    ].sort((a, b) => ((a as FirestorePlaceDoc).order ?? 0) - ((b as FirestorePlaceDoc).order ?? 0))
     .filter((s: FirestorePlaceDoc) => !s.hidden && hasLanguageContent(s, language));
    place.children_list = allChildren.map((s: FirestorePlaceDoc) => parseInt(s.id, 10) || s.id);
    place.children_layout = doc.spaceType || null;
  }

  return place;
};

export const getThumbnailData = async (placeId: number | string, language: string): Promise<Place | null> => {
  const key = cacheKey(placeId, language, 'thumb');
  const cached = getCached(key);
  if (cached) return cached;

  const strId = String(placeId);
  let doc: FirestorePlaceDoc | null = await findStationById(strId);
  if (!doc) doc = await findSpaceById(strId);
  if (!doc) return null;
  const place = toPlace(doc, language);
  if (place) setCache(key, place);
  return place;
};

export const getPlace = async (placeId: number | string, language: string, tourLanguage?: string): Promise<Place | null> => {
  const key = cacheKey(placeId, language, tourLanguage ? `tour:${tourLanguage}` : 'full');
  const cached = getCached(key);
  if (cached) return cached;

  const t0 = performance.now();
  const perf = (label: string) => console.log(`[perf] getPlace(${placeId}) ${label}: ${Math.round(performance.now() - t0)}ms`);

  const strId = String(placeId);
  let doc: FirestorePlaceDoc | null = await findStationById(strId);
  if (!doc) doc = await findSpaceById(strId);
  perf('findDoc');
  if (!doc) return null;

  const place = toPlace(doc, language);
  if (!place) return null;

  if (doc.storyIds && doc.storyIds.length > 0) {
    const stories = await findStoriesByStation(strId);
    perf('findStories');
    if (stories.length > 0) {
      const story = stories[0];
      const videoLang = tourLanguage
        ? resolveLang(story.content?.video || {}, tourLanguage)
        : resolveLang(story.content?.video || {}, language);
      const audioLang = resolveLang(story.content?.audio || {}, language);
      const t = place.translations[0];
      if (story.content?.audio?.[audioLang]) {
        t.audio = { id: extractAssetId(story.content.audio[audioLang].url)!, title: story.content.audio[audioLang].title || '' };
      }
      if (story.content?.video?.[videoLang]) {
        t.video = { id: extractAssetId(story.content.video[videoLang].url)!, title: story.content.video[videoLang].title || '' };
      }
      if (story.accessibility?.subtitles?.[audioLang]) {
        t.accessibility_hearing_video_subtitles = { id: extractAssetId(story.accessibility.subtitles[audioLang])! };
      }
    }
  }

  if (doc.spaceType && !doc.stationType) {
    const [childStations, childSpaces] = await Promise.all([
      findStationsBySpace(strId),
      findChildSpaces(strId),
    ]);
    perf('findChildren');
    const allChildren = [
      ...childSpaces.map((s: FirestorePlaceDoc) => ({ ...s, _isSpace: true })),
      ...childStations,
    ].sort((a, b) => ((a as FirestorePlaceDoc).order ?? 0) - ((b as FirestorePlaceDoc).order ?? 0))
     .filter((s: FirestorePlaceDoc) => !s.hidden && hasLanguageContent(s, language));

    place.children_list = allChildren.map((s: FirestorePlaceDoc) => parseInt(s.id, 10) || s.id);
    place.children = allChildren.map((s: FirestorePlaceDoc) => toPlace(s, language)).filter(Boolean) as Place[];
  }

  perf('total');
  setCache(key, place);
  return place;
};

export const getMapData = async (slug: string): Promise<Place[]> => {
  const site = await findSiteBySlug(slug);
  if (!site) return [];
  const stations = await findStationsWithLocation(site.id);
  return stations.map((s: FirestorePlaceDoc) => ({
    id: parseInt(s.id, 10) || s.id,
    location: s.location ? { type: 'Point' as const, coordinates: [s.location.lng, s.location.lat] as [number, number] } : null,
    status: s.status || 'published',
    parent_list_order: s.order ?? 0,
    information_photo_type: s.isPanoramic ? 'Panoramic' as const : 'Standard' as const,
    information_icon: s.iconUrl ? { id: extractAssetId(s.iconUrl)!, title: s.stationType === 'panoramic' ? 'V360' : '' } : null,
    translations: [],
    slug: slug,
  } as unknown as Place));
};

export const getPlaceByQuery = async (stationId: string, language: string, slug: string): Promise<[Place, (number | string)[]]> => {
  const site = await findSiteBySlug(slug);
  if (!site) throw new Error('Could not find this station. Please try again');
  const station = await findStationByKeypad(site.id, stationId);
  if (!station) throw new Error('Could not find this station. Please try again');

  const place = toPlace(station, language)!;
  const parentId = station.spaceId;
  let childrenList: (number | string)[] = [];
  if (parentId) {
    const siblings = await findStationsBySpace(parentId);
    childrenList = siblings.map((s: FirestorePlaceDoc) => parseInt(s.id, 10) || s.id);
  }
  return [place, childrenList];
};

export const getChildren = async (_collectionName: string, language: string, children: (number | string)[]): Promise<Place[]> => {
  if (!children || children.length === 0) return [];

  const results = await Promise.all(
    children.map(async (id) => {
      const key = cacheKey(id, language, 'thumb');
      const cached = getCached(key);
      if (cached) return cached;

      const strId = String(id);
      let doc: FirestorePlaceDoc | null = await findStationById(strId);
      if (!doc) doc = await findSpaceById(strId);
      if (doc && !hasLanguageContent(doc, language)) return null;
      const place = doc ? toPlace(doc, language) : null;
      if (place) setCache(key, place);
      return place;
    })
  );

  const filtered = results.filter((p): p is Place => p !== null);
  return filtered.sort((a, b) => {
    const aVal = a.parent_list_order ?? a.sort ?? a.id;
    const bVal = b.parent_list_order ?? b.sort ?? b.id;
    return (aVal as number) - (bVal as number);
  });
};
